<template>
  <div class="page-view">

    <BasePageHeader :title="$t('categories.title')" @back="$router.back()">
      <template #actions>
        <router-link v-if="isManagerOrAbove" to="/bulk-edit/categories" class="btn ghost small">{{ $t('bulkEdit.title') }}</router-link>
        <button v-if="isManagerOrAbove" class="btn primary sm" @click="openCreate">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          {{ $t('common.new') }}
        </button>
      </template>
    </BasePageHeader>

    <div class="px-4 pt-3 pb-2">
      <SearchField v-model="query" :debounce="0" :placeholder="$t('categories.searchPlaceholder')" />
    </div>

    <div v-if="formOpen" class="mx-4 my-2 p-4 rounded-xl flex flex-col gap-2.5 border border-[var(--color-border)]" style="background: var(--color-surface)">
      <div class="text-sm font-semibold">{{ $t('categories.newCategory') }}</div>
      <BaseFormField :label="$t('common.name')" required>
        <BaseInput v-model="form.name" :placeholder="$t('categories.namePlaceholder')" />
      </BaseFormField>
      <BaseFormField :label="$t('categories.parent')">
        <BaseSelect v-model="form.parent">
          <option :value="null">{{ $t('categories.noParent') }}</option>
          <option v-for="c in rootCategories" :key="c.id" :value="c.id">{{ c.name }}</option>
        </BaseSelect>
      </BaseFormField>
      <BaseFormActions
        :confirm-label="$t('common.create')"
        :loading="saving"
        :disabled="!form.name"
        @cancel="closeForm"
        @confirm="save"
      />
    </div>

    <EmptyState v-if="store.isLoadingCategories" :message="$t('common.loading')" />
    <EmptyState v-else-if="!filteredRoots.length && !query" :message="$t('categories.noCategories')" />
    <EmptyState v-else-if="!allFiltered.length && query" :message="$t('categories.noMatch')" />

    <div v-else class="list-stack">
      <template v-if="query">
        <!-- Flat results when searching -->
        <div v-for="cat in allFiltered" :key="cat.id" class="data-card cursor-pointer" @click="router.push(`/categories/${cat.id}`)">
          <div class="data-card__main">
            <div class="data-card__name">{{ cat.name }}</div>
            <div class="data-card__meta">
              <span v-if="cat.parent" class="badge badge-accent">{{ parentName(cat.parent) }}</span>
              <span v-if="itemCountForCategory(cat.id)" class="badge badge-muted">{{ $t('categories.itemCount', { count: itemCountForCategory(cat.id) }) }}</span>
            </div>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-[18px] h-[18px] text-[var(--color-muted)] shrink-0 mr-2"><polyline points="9 18 15 12 9 6"/></svg>
        </div>
      </template>

      <template v-else>
        <!-- Tree view when not searching -->
        <div v-for="root in filteredRoots" :key="root.id" class="flex flex-col">
          <div
            class="data-card cursor-pointer"
            :class="childrenOf(root.id).length ? 'rounded-b-none border-b-0' : ''"
            @click="router.push(`/categories/${root.id}`)"
          >
            <div class="data-card__main">
              <div class="data-card__name">{{ root.name }}</div>
              <div class="data-card__meta">
                <span v-if="childrenOf(root.id).length" class="badge badge-muted">{{ $t('categories.subcategoriesCount', { count: childrenOf(root.id).length }) }}</span>
                <span v-if="itemCountForCategory(root.id)" class="badge badge-accent">{{ $t('categories.itemCount', { count: itemCountForCategory(root.id) }) }}</span>
              </div>
            </div>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-[18px] h-[18px] text-[var(--color-muted)] shrink-0 mr-2"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
          <div v-if="childrenOf(root.id).length" class="flex flex-col">
            <div
              v-for="child in childrenOf(root.id)"
              :key="child.id"
              class="data-card rounded-none border-t border-[#0d1117] ml-5 last:rounded-b-xl cursor-pointer"
              @click="router.push(`/categories/${child.id}`)"
            >
              <div class="data-card__main">
                <div class="data-card__name">{{ child.name }}</div>
                <div v-if="itemCountForCategory(child.id)" class="data-card__meta">
                  <span class="badge badge-accent">{{ $t('categories.itemCount', { count: itemCountForCategory(child.id) }) }}</span>
                </div>
              </div>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-[18px] h-[18px] text-[var(--color-muted)] shrink-0 mr-2"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
          </div>
        </div>
      </template>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from '@/stores/store'
import { useToast } from '@/composables/useToast'
import { usePermissions } from '@/composables/usePermissions.js'
import { createCategory } from '@/api/api'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseFormActions from '@/components/base/BaseFormActions.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import SearchField from '@/components/search/SearchField.vue'
import { useI18n } from 'vue-i18n'

const store = useStore()
const router = useRouter()
const toast = useToast()
const { isManagerOrAbove } = usePermissions()
const { t } = useI18n()

const query = ref('')
const formOpen = ref(false)
const saving = ref(false)
const form = ref({ name: '', parent: null })

const categories = computed(() => store.categories)
const rootCategories = computed(() => categories.value.filter(c => c.parent === null))

function childrenOf(id) {
  return categories.value.filter(c => c.parent === id)
}

function parentName(id) {
  return categories.value.find(c => c.id === id)?.name ?? `#${id}`
}

function itemCountForCategory(catId) {
  return store.items.filter(m => {
    const id = typeof m.category === 'object' ? m.category?.id : m.category
    return String(id) === String(catId)
  }).length
}

const allFiltered = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return categories.value
  return categories.value.filter(c => c.name?.toLowerCase().includes(q))
})

const filteredRoots = computed(() => rootCategories.value)

onMounted(async () => {
  if (!store.categories.length && !store.isLoadingCategories) {
    await store.fetchCategories()
  }
  if (!store.items.length && !store.isLoadingItems) {
    store.fetchItems()
  }
})

function openCreate() {
  form.value = { name: '', parent: null }
  formOpen.value = true
}

function closeForm() {
  formOpen.value = false
}

async function save() {
  saving.value = true
  try {
    await createCategory(form.value)
    toast.success(t('categories.created'))
    await store.fetchCategories(true)
    closeForm()
  } catch {
    toast.error(t('categories.saveFailed'))
  } finally {
    saving.value = false
  }
}
</script>


